import { ColDef } from "ag-grid-community";
import { ColumnTypeEnum, createGroupColumn } from "../../components";
import moment from "moment";
import { TradesGroupingMode } from "../../store";
import Big from "big.js";

// Simple-mode columns for Trades page
// Groups can be: Date -> Underlying or Underlying -> Date
// Visible data columns: Quantity, Market Price
export const getSimpleTradesColumnDefinitions = (
  groupingMode: TradesGroupingMode
): ColDef[] => {
  // Define the two grouping columns
  const tradeDateColumn = createGroupColumn(
    "Trade Date",
    "tradeDate",
    ColumnTypeEnum.Date,
    {
      rowGroup: true,
      hide: true,
      sort: "asc",
      rowDrag: true,
      suppressColumnsToolPanel: true,
      menuTabs: ["generalMenuTab"],
      suppressMovable: true,
      suppressMenu: true,
      valueFormatter: (params) => {
        return moment(params.value).format("YYYY-MM-DD");
      },
      valueGetter: (params) => {
        return moment(params.data.tradeDate).format("YYYY-MM-DD");
      },
    }
  );

  const underlyingColumn = createGroupColumn(
    "Underlying",
    "underlyingInstrument.symbol",
    ColumnTypeEnum.Text,
    {
      rowGroup: true,
      hide: true,
      sort: "asc",
      rowDrag: true,
      suppressColumnsToolPanel: true,
      menuTabs: ["generalMenuTab"],
      valueGetter: (params) => {
        if (params?.data?.underlyingInstrument?.instrumentType === "Future") {
          return params.data.underlyingInstrument.name;
        } else {
          return params?.data?.underlyingInstrument?.symbol;
        }
      },
      suppressMovable: true,
      suppressMenu: true,
    }
  );

  // Determine column order based on grouping mode
  const groupColumns =
    groupingMode === "underlying-date"
      ? [underlyingColumn, tradeDateColumn]
      : [tradeDateColumn, underlyingColumn];

  return [
    ...groupColumns,

    createGroupColumn("Quantity", "quantity", ColumnTypeEnum.Numeric, {
      aggFunc: "none",
      menuTabs: ["generalMenuTab"],
      suppressMovable: true,
      suppressMenu: true,
      sortable: false,
      resizable: false,
    }),

    // We only have a single market price available in simple mode
    createGroupColumn(
      "Market Price",
      "priceData.price",
      ColumnTypeEnum.Currency,
      {
        cellRendererParams: { noFormatting: true },
        aggFunc: "none",
        menuTabs: ["generalMenuTab"],
        suppressMovable: true,
        suppressMenu: true,
        sortable: false,
        resizable: false,
        valueGetter: (params) => {
          return parseFloat(params.data?.priceData?.price) || 0;
        },
      }
    ),

    createGroupColumn("Trade price", "price", ColumnTypeEnum.Currency, {
      hide: false,
      cellRendererParams: {
        noFormatting: true,
      },
      menuTabs: ["generalMenuTab"],
      aggFunc: "none",
    }),

    // P&L = quantity * (tradePrice - marketPrice)
    createGroupColumn("P&L", "pl", ColumnTypeEnum.Currency, {
      valueGetter: (params) => {
        const multiplier = params.data?.instrument?.multiplier || 1;
        const quantity = new Big((params.data?.quantity || 0) * multiplier);
        const tradePrice = new Big(params.data?.price || 0).round(2);
        const marketPrice = new Big(
          parseFloat(params.data?.priceData?.price) || 0
        ).round(2);

        const buySell = (params.data?.buySell ?? "buy").toLowerCase();
        const result = new Big(quantity.toNumber())
          .times(new Big(tradePrice).minus(marketPrice))
          .toNumber();

        const costs = Math.abs(
          parseFloat(params.data?.fees || 0 + params.data?.commission || 0)
        );

        if (marketPrice > tradePrice) {
          const pnl =
            buySell === "buy"
              ? Math.abs(result) - costs
              : -(Math.abs(result) + costs);
          return pnl;
        } else {
          const pnl =
            buySell === "buy"
              ? -(Math.abs(result) + costs)
              : Math.abs(result) - costs;
          return pnl;
        }
      },
      cellRendererParams: { showDirection: true },
      aggFunc: "sum",
      menuTabs: ["generalMenuTab"],
      suppressMovable: true,
      suppressMenu: true,
      sortable: false,
      resizable: false,
    }),

    createGroupColumn(
      "Gross proceeds",
      "grossProceeds",
      ColumnTypeEnum.Currency,
      {
        cellRendererParams: { showDirection: true },
        menuTabs: ["generalMenuTab"],
      }
    ),

    createGroupColumn("Net proceeds", "netProceeds", ColumnTypeEnum.Currency, {
      cellRendererParams: { showDirection: true },
      menuTabs: ["generalMenuTab"],
    }),

    createGroupColumn("Fees", "fees", ColumnTypeEnum.Currency, {
      aggFunc: "sum",
      menuTabs: ["generalMenuTab"],
      valueGetter: (params) => {
        const data = params.data ?? params.node?.data ?? params.node?.aggData;
        if (!data) {
          return undefined;
        }
        return -Math.abs(parseFloat(data.fees + data.commission));
      },
    }),
    // strategy
    createGroupColumn("Strategy", "strategy", ColumnTypeEnum.Text, {
      menuTabs: ["generalMenuTab"],
    }),
  ];
};

// Keep a default export for backwards compatibility
export const simpleTradesColumnDefinitions =
  getSimpleTradesColumnDefinitions("date-underlying");
